import React, { useEffect, useRef, useState, useCallback } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { createGeminiResult, getJobsStatus, queryGeminiSummaryResult } from '@/api/subtitles';
import './GeminiContent.less';
import MeetStatus, { IMeetStatus } from './MeetStatus';
import Test from './test';

// 定义生成类型枚举
export enum GeminiGenerateType {
  SUMMARY = 'SUMMARY_PROMPT',
  TRANSCRIPTION = 'TRANSCRIPTION_PROMPT',
  TRANSLATE = 'TRANSLATE_PROMPT',
}

interface GeminiContentProps {
  isFinish: boolean;
  meetStatus: IMeetStatus;
  fileMergeId: string;
  generateType: GeminiGenerateType;
  targetLang: string;
  handleEndMeeting: () => void;
}

const GeminiContent: React.FC<GeminiContentProps> = ({
  isFinish,
  meetStatus,
  fileMergeId,
  targetLang,
  handleEndMeeting,
}) => {
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadMsg, setLoadMsg] = useState(t('meeting.queryingSummary'));
  const [content, setContent] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();

  const fetchGeminiResult = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { data: jobsStatus } } = await getJobsStatus({
        file_merge_id: fileMergeId,
        prompt_type: 'SUMMARY_PROMPT',
      });

      const jobStatus = jobsStatus.generate_job_details.status;

      // 生成失败是终态:必须停掉轮询并提示,否则会一直命中下面的 !== 'FINISH' 分支空转,表现为无限转圈
      if (jobStatus === 'FAILED' || jobStatus === 'ERROR') {
        clearInterval(timerRef.current);
        setLoading(false);
        setErrorMsg(t('meeting.getSummaryFailed'));
        return;
      }

      if (jobStatus === 'INIT') {
        setLoadMsg(t('meeting.summaryGenerating'));
        return;
      }

      if (jobStatus !== 'FINISH') {
        return;
      }

      clearInterval(timerRef.current);

      const { data: { data: { summary_text } } } = await queryGeminiSummaryResult({
        file_merge_id: fileMergeId,
      });

      if (!summary_text) {
        const { data: { data: { multimodal_summary_details: createResult } } } = await createGeminiResult({
          file_merge_id: fileMergeId,
          prompt_type: 'SUMMARY_PROMPT',
          target_lang: targetLang,
        });

        if (createResult) {
          setTimeout(fetchGeminiResult, 500);
        }
      } else {
        setContent(summary_text);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch Gemini result:', error);
      setErrorMsg(t('meeting.getSummaryFailed'));
    }
  }, [fileMergeId, targetLang, t]);

  useEffect(() => {
    if (isFinish && fileMergeId) {
      timerRef.current = setInterval(fetchGeminiResult, 3000);
      fetchGeminiResult();
    }

    return () => {
      setContent(null);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        setLoading(false);
      }
    };
  }, [fileMergeId, isFinish, fetchGeminiResult]);

  if (meetStatus !== 'FINISH') {
    return <MeetStatus meetStatus={meetStatus} handleEndMeeting={handleEndMeeting} contentType="summary" />;
  }

  if (errorMsg) {
    return (
      <div className="gemini-content">
        <div className="loading-container">
          <div className="loading-text error-text">{errorMsg}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="gemini-content">
      {loading ? (
        <div className="loading-container">
          <div className="loading-text">
            <LoadingOutlined />
            {loadMsg}
          </div>
        </div>
      ) : (
        <div className="content-container">
          <div className="markdown-content-title">
            {t('meeting.meetingSummary')}
          </div>
          <ReactMarkdown className="markdown-content" remarkPlugins={[remarkGfm]}>
            {content || ''}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default GeminiContent;
